import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import {
    getValueBetweenRangeError,
    handleIsValueWithinRangeOfMinAndMax,
} from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput.util';

import useFeatureFlag from '../useFeatureFlag';

import { HandleValueInCentsCallback } from './useConvertCurrencyToCentsDebounced.types';

type UseHandleMinMaxInput = {
    amountInCents: number;
    amountInCurrency: number;
    handleValue?: HandleValueInCentsCallback;
    min?: number;
    max?: number;
    errorMessage?: string;
    setErrorMessage?: Dispatch<SetStateAction<string>>;
    touched?: boolean;
};

export const maxLessThanMinError = 'Max is less than min';
export const defaultErrorMessageSeparator = ';';

function useHandleMinMax({
    min,
    max,
    errorMessage,
    setErrorMessage,
    touched,
    handleValue,
    amountInCents,
    amountInCurrency,
}: UseHandleMinMaxInput) {
    const minMaxNPICurrencyInputEnabled = useFeatureFlag('MIN_MAX_NPICURRENCYINPUT');
    const hasConversionCompleted = useMemo(() => {
        return handleValue && Math.round(amountInCents) === Math.round(amountInCurrency * 100);
    }, [amountInCents, amountInCurrency, handleValue]);

    const isWithinRangeOfMinAndMax = useMemo(() => {
        return min || max ? handleIsValueWithinRangeOfMinAndMax(amountInCents, min, max) : true;
    }, [amountInCents, max, min]);

    const isMaxLessThanMin = useMemo(() => {
        return Number.isFinite(max) && Number.isFinite(min) && max < min;
    }, [max, min]);

    useEffect(() => {
        if (minMaxNPICurrencyInputEnabled) {
            if (isMaxLessThanMin && !errorMessage?.includes(maxLessThanMinError)) {
                setErrorMessage(maxLessThanMinError);
            }
        }
    }, [errorMessage, isMaxLessThanMin, minMaxNPICurrencyInputEnabled, setErrorMessage]);

    useEffect(() => {
        if (minMaxNPICurrencyInputEnabled && hasConversionCompleted && setErrorMessage) {
            const newErrorMessage = getValueBetweenRangeError(min, max);

            const isErrorMessageAlreadyShown = errorMessage?.includes(newErrorMessage);
            if (isWithinRangeOfMinAndMax && isErrorMessageAlreadyShown) {
                // remove the error message as it is now within range
                const replacedTrimmedErrorMessage = errorMessage
                    ?.replace(newErrorMessage, '')
                    .trim();
                setErrorMessage(
                    replacedTrimmedErrorMessage?.length > 0
                        ? replacedTrimmedErrorMessage
                        : undefined
                );
            } else if (
                !isErrorMessageAlreadyShown &&
                !isWithinRangeOfMinAndMax &&
                // Form edge case: only if touched is defined check if it is true else ignore
                ((touched !== undefined && touched === true) || touched === undefined)
            ) {
                setErrorMessage(
                    !errorMessage
                        ? newErrorMessage
                        : errorMessage
                            ?.concat(`${defaultErrorMessageSeparator}${newErrorMessage}`)
                            .trim()
                );
            }
        }
    }, [
        errorMessage,
        max,
        min,
        setErrorMessage,
        minMaxNPICurrencyInputEnabled,
        touched,
        hasConversionCompleted,
        isWithinRangeOfMinAndMax,
    ]);
}

export default useHandleMinMax;
